import React, { type PropsWithChildren } from 'react';
import { ThemeProvider , type SearchCSS } from '@atlassian/search-dialog';
import { DialogExpansionContextProvider } from '../dialog-expansion-context/dialog-expansion-context';
import { QueryContextProvider } from '../query-context/query-context';
import { ProductRouter, useProductContext } from '../product-router/product-router';
import MessagesIntlProvider from '../../common/message-intl-provider/messages-intl-provider';
import { BuildVersionAnalyticContext } from '../../common/build-version-analytic-context/build-version-analytic-context';
import { SearchSessionProvider } from '../../common/search-session-provider/search-session-provider';
import { AggregatorClientContextProvider, type AggregatorClientFeatures } from '../aggregator-client-context/aggregator-client-context';
import { ABTestContextProvider } from '../ab-test-context/ab-test-context';
import ExperimentExposureHandler from '../../common/experiment-exposure-handler/experiment-exposure-handler';
import { DialogDismissedHandler } from '../../common/analytics/dialog-dismissed-handler/dialog-dismissed-handler';
import { type UserDetails } from '../../common/user-context';
import { UserProvider } from '../../common/user-context/user-context';
import { AsyncLoadOnQuery } from './async-load/async-load-on-query';
import { FilterContextHOC } from '../filters/filter-context/filter-context';
import { AutocorrectionProvider } from '../autocorrection/autocorrection-context';
import { DialogShownHandler } from '../../common/analytics/dialog-shown-handler/dialog-shown-handler';
import { type DialogFeatures, FeaturesProvider } from '../features-context/features-context';
import { QuickFindAnalyticContext } from '../../common/quick-find-analytic-context';
import { InputContext } from '../input-context/input-context';
import { RovoContextProvider } from '../../common/rovo-context/rovo-context';
import { type ParsedUrlQuery } from '../../common/shared/types';
import { QueryParamsProvider } from '../query-params-context/query-params-context';

export interface MetaContextProviderProps {
	/**
	 * Used to override the default AtlasKit Search Navigation CSS theming.
	 */
	theme?: SearchCSS;
	/**
	 * Flag which indicates the current open state of the dialog. Used internally within the dialog for analytics.
	 */
	isExpanded: boolean;
	/**
	 * Sets the current open state of the dialog.
	 */
	setIsExpanded: (value: boolean) => void;
	/**
	 * Cloud id used for the current abTest.
	 */
	abTestCloudId: string;
	/**
	 * Url for aggregator for quicksearch supported products.
	 */
	aggregatorUrl: string;
	/**
	 * Used to provide customisation options to internal clients which interact with aggregator.
	 */
	clientProviderFeatures?: AggregatorClientFeatures;
	/**
	 * Details for the current user.
	 */
	user?: UserDetails;
	/**
	 * The organization id of the current user.
	 */
	orgId?: string;

	dialogFeatures?: DialogFeatures;

	primaryProduct?: string;

	forwardRef?: React.Ref<HTMLInputElement>;

	/*
	 * This prop is used to render the Rovo tip banner in Quick Find
	 */
	isRovoEnabled?: boolean;

	/**
	 * Used to support Griffin navigation (nav4)
	 */
	queryParams?: ParsedUrlQuery;
}

export type Props = PropsWithChildren<MetaContextProviderProps>;

export const MetaContextProvider: ({ children, theme, setIsExpanded, isExpanded, abTestCloudId, aggregatorUrl, clientProviderFeatures, user, orgId, dialogFeatures, primaryProduct, forwardRef, isRovoEnabled, queryParams, }: Props) => React.JSX.Element = ({
	children,
	theme,
	setIsExpanded,
	isExpanded,
	abTestCloudId,
	aggregatorUrl,
	clientProviderFeatures,
	user,
	orgId,
	dialogFeatures,
	primaryProduct,
	forwardRef,
	isRovoEnabled,
	queryParams,
}: Props) => {
	return (
		<QueryParamsProvider queryParams={queryParams}>
			<FeaturesProvider dialogFeatures={dialogFeatures}>
				<UserProvider user={user} orgId={orgId}>
					<RovoContextProvider isEnabled={isRovoEnabled}>
						<MessagesIntlProvider>
							<QuickFindAnalyticContext isQuickFind={dialogFeatures?.quickFind?.enabled}>
								<BuildVersionAnalyticContext>
									<ThemeProvider partialSearchCSS={theme}>
										<DialogExpansionContextProvider
											isExpanded={isExpanded}
											setIsExpanded={setIsExpanded}
										>
											<ProductRouter primaryProduct={primaryProduct}>
												<AggregatorClientContextProvider
													abTestCloudId={abTestCloudId}
													aggregatorUrl={aggregatorUrl}
													features={clientProviderFeatures}
												>
													<ABTestContextProvider>
														<SearchSessionProvider sessionKey={`ssid_provider_${isExpanded}`}>
															{isExpanded ? <ExperimentExposureHandler /> : null}
															<QueryContextProvider>
																<FilterContextHOC>
																	<AutocorrectionProvider>
																		<InputContext forwardRef={forwardRef}>
																			<AsyncLoadOnQuery />
																			<DialogDismissedAndShownHandler isExpanded={isExpanded} />
																			{children}
																		</InputContext>
																	</AutocorrectionProvider>
																</FilterContextHOC>
															</QueryContextProvider>
														</SearchSessionProvider>
													</ABTestContextProvider>
												</AggregatorClientContextProvider>
											</ProductRouter>
										</DialogExpansionContextProvider>
									</ThemeProvider>
								</BuildVersionAnalyticContext>
							</QuickFindAnalyticContext>
						</MessagesIntlProvider>
					</RovoContextProvider>
				</UserProvider>
			</FeaturesProvider>
		</QueryParamsProvider>
	);
};

const DialogDismissedAndShownHandler = ({ isExpanded }: { isExpanded: boolean }) => {
	const { getActiveProduct } = useProductContext();
	const activeProduct = getActiveProduct();
	return (
		<>
			<DialogDismissedHandler isExpanded={isExpanded} activeProduct={activeProduct?.id ?? ''} />
			<DialogShownHandler isExpanded={isExpanded} activeProduct={activeProduct?.id ?? ''} />
		</>
	);
};
